import React, { useState, useEffect, useRef } from "react";
import { rankForAmount, overtakeAmount } from "./rankForAmount";
import { fetchPreview } from "@/api";
import { previewLabels, bareDomain, faviconFor } from "./listings";
import { sound } from "./sound";
import { X, Link2, MessageSquare, Loader2, Zap } from "lucide-react";

const MIN_AMOUNT = 1;

const isValidUrl = (raw) => {
  try {
    const u = new URL(raw.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * BidOverlay: GMH-skinned bid modal. One overlay for TRANSMIT, List your link,
 * and Take #N. Replaces BidModal + desktop BidCard.
 *
 * Props:
 *   open, onClose, onSubmit, board, error, busy
 *   initialAmount (optional seed — e.g. overtake for a rank)
 *   initialUrl (optional prefill from the URL launch bar)
 */
export const BidOverlay = ({
  open,
  onClose,
  onSubmit,
  board = [],
  error = "",
  busy = false,
  initialAmount = 0,
  initialUrl = "",
}) => {
  const [form, setForm] = useState({
    url: initialUrl,
    message: "",
    amount: Number(initialAmount) > 0 ? Number(initialAmount) : 1,
  });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const lastPreviewUrl = useRef("");

  // reset when overlay (re)opens
  useEffect(() => {
    if (open) {
      setForm({
        url: initialUrl,
        message: "",
        amount: Number(initialAmount) > 0 ? Number(initialAmount) : 1,
      });
      setPreview(null);
      setPreviewLoading(false);
      setLocalError("");
      setEditing(false);
      lastPreviewUrl.current = "";
    }
  }, [open, initialUrl, initialAmount]);

  const amountNum = Number(form.amount) || 0;
  const projectedRank = rankForAmount(amountNum, board);
  const takeRank1 = overtakeAmount(board, amountNum);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadPreview = async (rawUrl) => {
    const url = (rawUrl || "").trim();
    if (!isValidUrl(url)) {
      lastPreviewUrl.current = "";
      setPreview(null);
      return;
    }
    if (url === lastPreviewUrl.current) return;
    lastPreviewUrl.current = url;
    setPreviewLoading(true);
    try {
      const meta = await fetchPreview(url);
      if (lastPreviewUrl.current !== url) return; // stale: a newer URL already won
      const labels = previewLabels(url, meta || {});
      const hostname = bareDomain(url) || url;
      setPreview({
        name: labels.name,
        title: labels.subtitle || (meta && meta.title) || "",
        description: labels.description,
        hostname,
        image: (meta && meta.image) || "",
        favicon: faviconFor(url),
      });
      setEditName(labels.name);
      setEditDesc(labels.description);
    } catch {
      if (lastPreviewUrl.current !== url) return;
      // OG fetch threw: still show the bar with the hostname, never hide it.
      const hostname = bareDomain(url) || url;
      setPreview({
        name: hostname,
        title: "",
        description: "",
        hostname,
        image: "",
        favicon: faviconFor(url),
      });
      setEditName(hostname);
      setEditDesc("");
    } finally {
      if (lastPreviewUrl.current === url) setPreviewLoading(false);
    }
  };

  // fetch whenever the URL becomes valid: prefilled on open, or typed.
  // (Blur also fetches explicitly via onUrlBlur below.)
  useEffect(() => {
    if (!open) return;
    loadPreview(form.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.url]);

  const onUrlBlur = (e) => {
    setForm((f) => ({ ...f, url: e.target.value }));
    loadPreview(e.target.value);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!isValidUrl(form.url)) {
      setLocalError("Enter a valid link (must start with http:// or https://).");
      return;
    }
    const amount = Math.floor(Number(form.amount));
    if (!Number.isFinite(amount) || amount < MIN_AMOUNT) {
      setLocalError(`Minimum fuel is $${MIN_AMOUNT}.`);
      return;
    }
    setLocalError("");
    onSubmit({
      url: form.url.trim(),
      amount,
      message: form.message.trim() || undefined,
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[#02040a]/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="bid-overlay-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="bid-overlay"
        className="panel corner-tick listing-board max-h-[90vh] w-full max-w-md overflow-y-auto p-6"
      >
        <div className="mb-5 flex items-start justify-between">
          <div className="mono text-[10px] tracking-[0.3em] text-gmh-cyan">YOUR BID</div>
          <button
            type="button"
            data-testid="bid-overlay-close"
            onClick={onClose}
            className="text-slate-500 transition-colors hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        <div data-testid="bid-overlay-rank" className="mb-3 text-2xl font-bold text-white">
          THIS BID LANDS AT #{projectedRank}.
        </div>

        <div className="mb-2 flex items-center justify-between">
          <span className="mono text-[10px] tracking-[0.25em] text-slate-500">AMOUNT</span>
          <span className="mono text-[10px] text-slate-500">
            {takeRank1 ? `#1 costs $${takeRank1.toLocaleString()}` : "You're at #1"}
          </span>
        </div>

        <div className="mb-2 flex items-center gap-3">
          <button
            type="button"
            data-testid="bid-overlay-amount-dec"
            onClick={() => {
              sound.blip();
              setForm((f) => ({ ...f, amount: Math.max(MIN_AMOUNT, (Number(f.amount) || 1) - 1) }));
            }}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-[#1c2740] text-slate-400 hover:border-gmh-cyan/50 hover:text-gmh-cyan"
          >
            −
          </button>
          <div className="relative flex-1">
            <span className="mono absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gmh-orange">$</span>
            <input
              data-testid="bid-overlay-amount"
              type="number"
              min={MIN_AMOUNT}
              value={form.amount}
              onChange={set("amount")}
              className="field mono w-full py-2.5 pl-7 pr-3 text-sm"
            />
          </div>
          <button
            type="button"
            data-testid="bid-overlay-amount-inc"
            onClick={() => {
              sound.blip();
              setForm((f) => ({ ...f, amount: (Number(f.amount) || 0) + 1 }));
            }}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-[#1c2740] text-slate-400 hover:border-gmh-cyan/50 hover:text-gmh-cyan"
          >
            +
          </button>
        </div>

        {takeRank1 && (
          <div className="mb-4">
            <div className="h-1 overflow-hidden rounded-full bg-[#1c2740]">
              <div
                className="h-full bg-gmh-orange"
                style={{ width: `${Math.min(100, (amountNum / takeRank1) * 100)}%` }}
              />
            </div>
            <p className="mono mt-1 text-[10px] text-slate-600">
              ${amountNum.toLocaleString()} of the ${takeRank1.toLocaleString()} it takes to reach #1.
            </p>
          </div>
        )}

        <form onSubmit={submit} noValidate>
          <label className="mono mb-1.5 block text-[10px] tracking-[0.25em] text-slate-500">
            <Link2 size={11} className="mr-1 inline" /> YOUR LINK *
          </label>
          <input
            data-testid="bid-overlay-url-input"
            value={form.url}
            onChange={set("url")}
            onBlur={onUrlBlur}
            placeholder="https://yoursite.com"
            className="field mono mb-2 w-full px-3 py-2.5 text-sm"
          />

          {previewLoading && (
            <div
              data-testid="bid-overlay-preview-loading"
              className="mb-3 flex items-center gap-2 rounded-lg border border-[#1c2740] bg-[#0b1120]/60 px-3 py-2.5 text-slate-400"
            >
              <Loader2 size={14} className="animate-spin" />
              <span className="mono text-[11px]">Pulling link preview…</span>
            </div>
          )}

          {!previewLoading && preview && (
            <div
              data-testid="bid-overlay-preview"
              className="listing-card mb-3 flex items-start gap-3 rounded-lg border border-[#1c2740] bg-[#0b1120]/60 p-3"
            >
              {preview.image || preview.favicon ? (
                <img
                  data-testid="bid-overlay-preview-image"
                  src={preview.image || preview.favicon}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded-md bg-white/10 object-cover"
                />
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/10 text-slate-500">
                  <Link2 size={16} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div data-testid="bid-overlay-preview-title" className="truncate text-sm font-semibold text-white">
                  {preview.name}
                </div>
                <div data-testid="bid-overlay-preview-host" className="mono truncate text-[11px] text-gmh-cyan">
                  {preview.hostname}
                </div>
                {preview.title && preview.title !== preview.name && (
                  <div className="line-clamp-1 text-[11px] leading-snug text-slate-300">
                    {preview.title}
                  </div>
                )}
                <div className="line-clamp-2 text-[11px] leading-snug text-slate-400">
                  {preview.description || "No description found on this link."}
                </div>
              </div>
              <button
                type="button"
                data-testid="bid-overlay-edit"
                onClick={() => setEditing((e) => !e)}
                className="mono text-[10px] tracking-[0.15em] text-gmh-cyan hover:underline"
              >
                {editing ? "DONE" : "EDIT"}
              </button>
            </div>
          )}

          {editing && (
            <div className="mb-3 space-y-2">
              <input
                data-testid="bid-overlay-name-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Name"
                className="field mono w-full px-3 py-2 text-sm"
              />
              <textarea
                data-testid="bid-overlay-desc-input"
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                placeholder="Description"
                rows={2}
                className="field mono w-full resize-none px-3 py-2 text-sm"
              />
              <label className="mono mb-1.5 block text-[10px] tracking-[0.25em] text-slate-500">
                <MessageSquare size={11} className="mr-1 inline" /> MESSAGE TO SAM
              </label>
              <input
                data-testid="bid-overlay-message-input"
                value={form.message}
                onChange={set("message")}
                placeholder="Hang in there! (optional)"
                className="field mono w-full px-3 py-2.5 text-sm"
              />
            </div>
          )}

          {localError && (
            <p
              data-testid="bid-overlay-error"
              role="alert"
              className="mono mb-3 border border-[#ff6b6b]/40 bg-[#ff6b6b]/10 px-3 py-2 text-[11px] text-[#ff8f8f]"
            >
              {localError}
            </p>
          )}

          {error && (
            <p
              data-testid="bid-overlay-checkout-error"
              role="alert"
              className="mono mb-3 rounded-lg border border-[#ff6b6b]/40 bg-[#ff6b6b]/10 px-3 py-2 text-[11px] text-[#ff8f8f]"
            >
              {error}
            </p>
          )}

          <p
            data-testid="bid-fiction-disclaimer"
            className="mono mb-4 border border-[#1c2740] bg-[#0b1120]/60 px-3 py-2 text-[10px] leading-relaxed tracking-wide text-slate-500"
          >
            GetMeHome.lol is fiction and an art project. Your payment buys a ranked listing. It is not a donation, charity, investment, or rescue — no real person is being rescued.
          </p>

          <button
            data-testid="bid-overlay-submit"
            type="submit"
            disabled={busy}
            onClick={() => sound.blip()}
            className="cta-fuel mono flex w-full items-center justify-center gap-2 whitespace-nowrap py-3.5 text-xs font-bold tracking-[0.3em] text-[#2a1000]"
          >
            <Zap size={14} /> CONTINUE TO PAYMENT · ${amountNum}
          </button>
          <button
            type="button"
            data-testid="bid-overlay-cancel"
            onClick={onClose}
            className="mono mt-2 w-full border border-[#1c2740] py-2.5 text-[11px] font-semibold tracking-[0.2em] text-slate-400 hover:border-gmh-cyan/50 hover:text-gmh-cyan"
          >
            CANCEL
          </button>
        </form>
      </div>
    </div>
  );
};
